import type { CSSProperties } from "react";

/**
 * Sistema de diseño de Argus — fuente única de paleta, estilos base y helpers
 * de formato. Deriva de los activos de marca (`marca/visual/referencias/`):
 * tiel del logo, crema de fondo, serif del wordmark solo para display.
 * Ninguna vista define su propia paleta; todas importan de aquí (issue #47).
 */

export const C = {
  // Marca
  tiel: "#0F5257",
  tielDark: "#0B3E42",
  tielSoft: "#E5EEED",
  // Neutrales (crema/tinta, con sesgo cálido hacia la marca)
  bg: "#F7F4EC",
  rail: "#0B3E42",
  line: "#E5DFD0",
  ink: "#13343B",
  muted: "#4A6167",
  grayBg: "#EFEBE0",
  // Semáforo único (ok · alerta · crítico) — también lo usa Semaforo.tsx
  green: "#1E7A4E",
  greenBg: "#E8F3EC",
  amber: "#9C6410",
  amberBg: "#FBF3E3",
  red: "#A93B2A",
  redBg: "#F9ECE9",
  // Informativo (armonizado, no compite con el tiel de acción)
  blue: "#3A648C",
  blueBg: "#E9EFF5",
  // Acento cálido (pupila del logo en el rail, indicador de sección activa)
  oro: "#E8C67A",
} as const;

/** Serif garalde para display — eco del wordmark ARGUS. Solo títulos y cifras clave. */
export const SERIF = '"Palatino Linotype", Palatino, "Book Antiqua", Georgia, "Times New Roman", serif';
export const SANS = 'system-ui, -apple-system, "Segoe UI", Roboto, sans-serif';

export const st = {
  input: {
    width: "100%",
    boxSizing: "border-box",
    padding: "9px 12px",
    border: `1px solid ${C.line}`,
    borderRadius: 10,
    fontSize: 14,
    background: "#fff",
    color: C.ink,
  } as CSSProperties,
  label: { fontSize: 12.5, fontWeight: 600, color: C.ink, display: "block", marginBottom: 4 } as CSSProperties,
  /** Mensaje de validación pegado al input que falla (rojo, debajo del campo). */
  errorCampo: { margin: "4px 0 0", fontSize: 11.5, color: C.red, lineHeight: 1.35 } as CSSProperties,
  btn: {
    padding: "9px 14px",
    borderRadius: 10,
    border: `1px solid ${C.line}`,
    background: "#fff",
    color: C.ink,
    fontSize: 13,
    fontWeight: 600,
    cursor: "pointer",
  } as CSSProperties,
  btnPrim: {
    padding: "10px 16px",
    borderRadius: 10,
    border: "none",
    background: C.tiel,
    color: "#fff",
    fontSize: 14,
    fontWeight: 700,
    cursor: "pointer",
  } as CSSProperties,
  tarjeta: {
    background: "#fff",
    border: `1px solid ${C.line}`,
    borderRadius: 14,
    padding: "14px 16px",
    marginBottom: 12,
  } as CSSProperties,
  eyebrow: {
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    color: C.muted,
    margin: "0 0 8px",
  } as CSSProperties,
  /** Título de vista — serif de marca. Aplicar al heading principal de cada sección. */
  display: {
    fontFamily: SERIF,
    fontWeight: 500,
    letterSpacing: "0.01em",
  } as CSSProperties,
} as const;

/** Monto en pesos con formato es-MX. Fallback tipográfico único: "—". */
export function dinero(v: number | string | null | undefined): string {
  if (v === null || v === undefined || v === "") return "—";
  const n = typeof v === "number" ? v : Number(v);
  if (!Number.isFinite(n)) return "—";
  return n.toLocaleString("es-MX", { style: "currency", currency: "MXN" });
}

const MESES_CORTOS = ["ene", "feb", "mar", "abr", "may", "jun", "jul", "ago", "sep", "oct", "nov", "dic"];

/**
 * Fecha ISO (AAAA-MM-DD o timestamp) → "17 jul 2026". Solo para RENDER: los
 * valores que viajan a la API o alimentan inputs type="date" siguen en ISO.
 * Parseo textual deliberado — nada de `new Date(iso)` con su corrimiento UTC.
 */
export function fecha(iso: string | null | undefined): string {
  if (!iso) return "—";
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!m) return iso;
  const [, anio, mesNum, dia] = m;
  if (anio === undefined || mesNum === undefined || dia === undefined) return iso;
  const mes = MESES_CORTOS[Number(mesNum) - 1];
  if (mes === undefined) return iso;
  return `${String(Number(dia))} ${mes} ${anio}`;
}
